import type { FingeringStorageResult } from './fingeringStorage';
import type { FingeringMode, FingeringPart } from '../types/fingering';
import {
  buildBaseName,
  buildResultFileName,
  buildOmfzFileName,
  partNumFromPart,
  resolveOriginalTitle,
} from './fileNaming';
import { PART_LABELS } from '../constants/fingering';
import {
  ARCHIVE_MAX_UNCOMPRESSED_BYTES,
  ARCHIVE_SCHEMA_VERSION,
  ArchiveCreatedBy,
  ArchiveError,
  ArchiveFileKey,
  ArchiveManifest,
} from '../types/driveArchive';

export interface CreateFingeringZipOptions {
  /**
   * issue #152: **元タイトル** (アップロード名から拡張子を除いたもの)。 完成 PDF 名を渡しては
   * ならない (`_運指済` / part label が二重付与される)。 manifest.project.title にそのまま書き、
   * omfz 名は (元タイトル, part, mode) の純関数で組む。 呼出し側は resolveOriginalTitle で解決する。
   */
  title: string;
  part: string;
  /** issue #78: 譜面モード。 'part' はパート譜 (fileName から part を省略)、 'score' は総譜 (part を含む)。 */
  mode: FingeringMode;
  appVersion: string;
  source: string;
  savedByDevice: 'desktop' | 'mobile';
  tuning?: string;
  compatibilityMinAppVersion?: string;
  fingeringSchemaVersion?: string;
  now?: Date;
  /**
   * issue #66: 保存物の作成起源を識別 (optional、 後方互換)。
   * 未指定なら manifest に書き込まず、 復元時は 'manual' default に解釈される。
   */
  createdBy?: ArchiveCreatedBy;
}

export interface CreatedArchive {
  blob: Blob;
  fileName: string;
  manifest: ArchiveManifest;
}

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(bytes));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function blobSha256(blob: Blob): Promise<{ bytes: ArrayBuffer; sha256: string }> {
  const bytes = await blob.arrayBuffer();
  const sha256 = await sha256Hex(bytes);
  return { bytes, sha256 };
}

export async function createFingeringZip(
  stored: FingeringStorageResult,
  options: CreateFingeringZipOptions,
): Promise<CreatedArchive> {
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  const filesDir = zip.folder('files');
  if (!filesDir) throw new ArchiveError('unknown', 'Failed to create files/ directory in zip');

  const savedAt = (options.now ?? new Date()).toISOString();
  const manifest: ArchiveManifest = {
    schema_version: ARCHIVE_SCHEMA_VERSION,
    app: {
      name: 'おまかせ運指くん',
      version: options.appVersion,
      source: options.source,
    },
    project: {
      title: options.title,
      part: options.part,
      tuning: options.tuning,
      detected_bpm: stored.detectedBpm,
      saved_at: savedAt,
      saved_by_device: options.savedByDevice,
      // issue #66: created_by 未指定なら undefined のまま (JSON.stringify が skip)
      created_by: options.createdBy,
      // issue #81: 譜面モードを書き込み、 .omfz 復元で保存ボタンを再 enable できるようにする
      mode: options.mode,
    },
    files: {},
    compatibility: {
      min_app_version: options.compatibilityMinAppVersion ?? '2026-04-01',
      fingering_schema_version: options.fingeringSchemaVersion ?? '2.0',
    },
    // issue #91 round2 (#41): description の part 表示も PART_LABELS (1st-4th) に変換し mode で出し分ける。
    // mode='part' (パート譜) は part 省略、 'score' (総譜) は (1st-4th) を付与。 生 Guitar1 は出さない。
    description: `${options.title}${
      options.mode === 'part'
        ? ''
        : ` ${PART_LABELS[options.part as FingeringPart] ?? options.part}`
    }${
      stored.detectedBpm != null ? ` / ${stored.detectedBpm}bpm` : ''
    } / ${savedAt.slice(0, 10)} 保存`,
  };

  async function addEntry(
    key: keyof ArchiveManifest['files'],
    path: string,
    blob: Blob,
    required: boolean,
  ): Promise<void> {
    const { bytes, sha256 } = await blobSha256(blob);
    filesDir!.file(path, bytes, { compression: 'STORE' });
    manifest.files[key] = {
      path: `files/${path}`,
      size: blob.size,
      sha256,
      required,
    };
  }

  await addEntry('fingered_pdf', 'fingered.pdf', stored.blob, true);
  if (stored.fingeringJson == null) {
    throw new ArchiveError('invalid_manifest', 'fingering.json is required to build an archive');
  }
  const fingeringBlob = new Blob([stored.fingeringJson], { type: 'application/json' });
  await addEntry('fingering_json', 'fingering.json', fingeringBlob, true);
  if (!stored.originalPdfBlob) {
    throw new ArchiveError('invalid_manifest', 'original PDF is required to build an archive');
  }
  await addEntry('original_pdf', 'original.pdf', stored.originalPdfBlob, true);
  if (stored.audioBlob) await addEntry('audio_mp3', 'audio.mp3', stored.audioBlob, false);
  // issue #46 Phase 1: score.mxl は再生成 (regeneratePdf/Audio/Both) に必須のため required 化。
  // 後方互換なし、 mxl を持たない古い保存形式の扱いは別 issue で対応 (Phase 2 不採用)。
  if (!stored.mxlBlob) {
    throw new ArchiveError('invalid_manifest', 'score.mxl is required to build an archive');
  }
  await addEntry('score_mxl', 'score.mxl', stored.mxlBlob, true);

  zip.file('manifest.json', JSON.stringify(manifest, null, 2), { compression: 'STORE' });
  zip.file(
    'README.txt',
    'おまかせ運指くん アーカイブ\n' +
      'manifest.json を参照してください。\n',
    { compression: 'STORE' },
  );

  const blob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
  // issue #91: ファイル名規則を fileNaming (backend file_naming.py と共通規則) に統一。
  // 日時 stamp は廃止。 パート譜 (mode='part'): <base>.omfz / 総譜 (mode='score'): <base>_<1st-4th>.omfz。
  const fileName = buildOmfzFileName(
    buildBaseName(options.title),
    options.part as FingeringPart,
    options.mode,
  );

  return { blob, fileName, manifest };
}

const ALLOWED_PATHS = new Set([
  'manifest.json',
  'files/fingered.pdf',
  'files/audio.mp3',
  'files/fingering.json',
  'files/original.pdf',
  'files/score.mxl',
  'README.txt',
]);

export interface ExtractOptions {
  verifyChecksums?: boolean;
}

export interface ExtractedArchive {
  stored: FingeringStorageResult;
  manifest: ArchiveManifest;
  /**
   * issue #66: 作成起源 (manual/auto)。 manifest.project.created_by 未設定の
   * 古い .omfz は 'manual' default に解釈する (後方互換)。
   */
  createdBy: ArchiveCreatedBy;
}

// issue #91: 復元時のファイル名も fileNaming (backend file_naming.py と共通規則) で生成する。
// mode='part' は part 番号なし (<base>_運指済.<ext>)、 mode='score' は 1st-4th 付き。
// issue #152: 起点は正規化済みの「元タイトル」。 legacy .omfz は manifest.project.title 自体が
// 汚染 (`_運指済` / `unknown`) されていることがあるため、 extract 側で derive 正規化してから渡す。
function buildRestoredFileName(
  originalTitle: string,
  part: string | undefined,
  mode: FingeringMode,
  ext: 'pdf' | 'mp3',
): string {
  return buildResultFileName(buildBaseName(originalTitle), partNumFromPart(part), mode, ext);
}

export async function extractFingeringZip(
  blob: Blob,
  opts: ExtractOptions = {},
): Promise<ExtractedArchive> {
  const { default: JSZip } = await import('jszip');
  const zip = await JSZip.loadAsync(blob);
  const manifestEntry = zip.file('manifest.json');
  if (!manifestEntry) throw new ArchiveError('missing_manifest', 'manifest.json not found');

  let manifest: ArchiveManifest;
  try {
    manifest = JSON.parse(await manifestEntry.async('string')) as ArchiveManifest;
  } catch (e) {
    throw new ArchiveError('invalid_manifest', 'manifest.json is not valid JSON', e);
  }
  if (!manifest.schema_version?.startsWith('1.')) {
    throw new ArchiveError('unsupported_schema', `Unsupported schema_version ${manifest.schema_version}`);
  }

  const declaredTotal = Object.values(manifest.files).reduce((sum, f) => sum + (f?.size ?? 0), 0);
  if (declaredTotal > ARCHIVE_MAX_UNCOMPRESSED_BYTES) {
    throw new ArchiveError('oversize', `Declared total ${declaredTotal} exceeds limit`);
  }

  let total = 0;
  async function readBlob(path: string, mime: string): Promise<Blob | undefined> {
    if (!ALLOWED_PATHS.has(path)) return undefined;
    const entry = zip.file(path);
    if (!entry) return undefined;
    const buf = await entry.async('arraybuffer');
    total += buf.byteLength;
    if (total > ARCHIVE_MAX_UNCOMPRESSED_BYTES) {
      throw new ArchiveError('oversize', 'Uncompressed total exceeds limit');
    }
    if (opts.verifyChecksums) {
      const actual = await sha256Hex(buf);
      const entryKey = Object.entries(manifest.files).find(
        ([, v]) => v?.path === path,
      )?.[0];
      const expected = entryKey ? manifest.files[entryKey as ArchiveFileKey]?.sha256 : undefined;
      if (expected && expected !== actual) {
        throw new ArchiveError('checksum_mismatch', `${path} sha256 mismatch`);
      }
    }
    return new Blob([buf], { type: mime });
  }

  const fingeredBlob = await readBlob('files/fingered.pdf', 'application/pdf');
  const fingeringBlob = await readBlob('files/fingering.json', 'application/json');
  const originalBlob = await readBlob('files/original.pdf', 'application/pdf');
  const audioBlob = await readBlob('files/audio.mp3', 'audio/mpeg');
  const mxlBlob = await readBlob('files/score.mxl', 'application/vnd.recordare.musicxml');

  if (!fingeredBlob || !fingeringBlob || !originalBlob) {
    throw new ArchiveError('invalid_manifest', 'Required archive entries are missing');
  }
  const fingeringJson = await fingeringBlob.text();
  // issue #81 追補❸: mode が manifest に無い古い .omfz は 'part' に既定値補完する。
  // undefined のままだと DriveSaveButton の canSave (!!stored.mode) が false で保存ボタンが
  // 永久グレーアウトするため。 created_by ?? 'manual' と同じ後方互換パターン。
  // issue #91: 復元時のファイル名生成にも同じ mode を渡し、 mode 別命名規則を適用する。
  const restoredMode: FingeringMode = manifest.project.mode ?? 'part';
  // issue #152: manifest.project.title は元タイトル契約 (v2)。 ただし legacy .omfz は完成 PDF 名を
  // title に流用した汚染値のことがあるため、 metaTitle スロット (= derive で正規化される経路) に
  // 通してから命名ソースにする。 これで復元経路も 1 回で正規名へ収束する (自己治癒)。
  const originalTitle = resolveOriginalTitle({ metaTitle: manifest.project.title });
  const stored: FingeringStorageResult = {
    blob: fingeredBlob,
    fileName: buildRestoredFileName(originalTitle, manifest.project.part, restoredMode, 'pdf'),
    originalTitle: originalTitle || undefined,
    audioBlob,
    audioFileName: audioBlob
      ? buildRestoredFileName(originalTitle, manifest.project.part, restoredMode, 'mp3')
      : undefined,
    fingeringJson,
    originalPdfBlob: originalBlob,
    mxlBlob,
    detectedBpm: manifest.project.detected_bpm,
    mode: restoredMode,
  };
  // issue #66: created_by が manifest に書かれていない古い .omfz は 'manual' default に解釈。
  const createdBy: ArchiveCreatedBy = manifest.project.created_by ?? 'manual';
  return { stored, manifest, createdBy };
}

